import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { TrendingUp, CheckCircle, AlertTriangle, Calendar, Target, Loader2, MessageSquare } from 'lucide-react';

const ComplaintPerformance = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = authService.getCurrentUser();

  useEffect(() => {
    fetchMyStats();
  }, []);

  const fetchMyStats = async () => {
    try {
      const response = await api.getMyPerformance('complaints');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch performance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const dailyResolution = stats?.daily_total > 0 
    ? ((stats?.daily_solved || 0) / stats.daily_total * 100).toFixed(1) : 0;
  const monthlyResolution = stats?.monthly_total > 0 
    ? ((stats?.monthly_solved || 0) / stats.monthly_total * 100).toFixed(1) : 0;
  const overallResolution = stats?.total_complaints > 0 
    ? ((stats?.total_solved || 0) / stats.total_complaints * 100).toFixed(1) : 0;

  const isEntryAgent = user?.role === 'complaint_entry';
  const isSolver = user?.role === 'complaint_solver';

  return (
    <div data-testid="complaint-performance" className="max-w-[1200px] mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-headings text-slate-900 mb-2">
          My Performance
        </h1>
        <p className="text-slate-600 font-body">
          Hello {user?.name}, here's your {isEntryAgent ? 'complaint entry' : 'complaint resolution'} performance.
        </p>
      </div>

      {/* Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Daily */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={20} />
            <span className="text-sm font-medium uppercase">Today</span>
          </div>
          {isSolver ? (
            <>
              <p className="text-4xl font-bold">{dailyResolution}%</p>
              <p className="text-orange-100 text-sm mt-2">
                {stats?.daily_solved || 0} solved / {stats?.daily_total || 0} assigned
              </p>
            </>
          ) : (
            <>
              <p className="text-4xl font-bold">{stats?.daily_created || 0}</p>
              <p className="text-orange-100 text-sm mt-2">Complaints registered today</p>
            </>
          )}
        </div>

        {/* Monthly */}
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <Target size={20} />
            <span className="text-sm font-medium uppercase">This Month</span>
          </div>
          {isSolver ? (
            <>
              <p className="text-4xl font-bold">{monthlyResolution}%</p>
              <p className="text-amber-100 text-sm mt-2">
                {stats?.monthly_solved || 0} solved / {stats?.monthly_total || 0} assigned
              </p>
            </>
          ) : (
            <>
              <p className="text-4xl font-bold">{stats?.monthly_created || 0}</p>
              <p className="text-amber-100 text-sm mt-2">Complaints registered this month</p>
            </>
          )}
        </div>

        {/* Overall */}
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={20} />
            <span className="text-sm font-medium uppercase">Overall</span>
          </div>
          {isSolver ? (
            <>
              <p className="text-4xl font-bold">{overallResolution}%</p>
              <p className="text-teal-100 text-sm mt-2">
                {stats?.total_solved || 0} solved / {stats?.total_complaints || 0} total
              </p>
            </>
          ) : (
            <>
              <p className="text-4xl font-bold">{stats?.total_created || 0}</p>
              <p className="text-teal-100 text-sm mt-2">Total complaints registered</p>
            </>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      {isSolver && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs uppercase text-slate-500 font-bold mb-1">Assigned to Me</p>
            <p className="text-2xl font-bold text-slate-900">{stats?.total_complaints || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs uppercase text-slate-500 font-bold mb-1">Solved</p>
            <p className="text-2xl font-bold text-emerald-600">{stats?.total_solved || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs uppercase text-slate-500 font-bold mb-1">In Progress</p>
            <p className="text-2xl font-bold text-amber-600">{stats?.in_progress || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs uppercase text-slate-500 font-bold mb-1">Escalated</p>
            <p className="text-2xl font-bold text-red-600">{stats?.escalated || 0}</p>
          </div>
        </div>
      )}

      {isEntryAgent && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold font-headings text-slate-900 mb-4">Complaints by Problem Type</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {(stats?.by_problem_type || []).map((item) => (
              <div key={item._id} className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700">{item._id || 'Unknown'}</p>
                <p className="text-xl font-bold text-slate-900">{item.count}</p>
              </div>
            ))}
            {(!stats?.by_problem_type || stats.by_problem_type.length === 0) && (
              <p className="text-slate-500 col-span-3 text-center py-4">No complaints registered yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintPerformance;
